import React, { lazy, Suspense, useContext } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { useAuth } from '@zopsmart/zs-components';

import CompleteLoader from '../Components/Loader/CompletePageLoader';

import useWindowSize from '../Common/Hook/useWindowSize';

import { componentLazyLoader, isLogin } from '../utils';

import {
  ACCESS_DENIED_ENDPOINT,
  ACCESS_TOKEN_ENDPOINT,
  AGENCIES_ENDPOINT,
  ALLOWED_RETRY_LIMIT,
  APPLICANT_DETAILS_ENDPOINT,
  APPLICATION_OVERVIEW_ENDPOINT,
  APPLICATIONS_DETAILS_ENDPOINT,
  APPLICATIONS_ENDPOINT,
  APPLY,
  AuthContext,
  CALENDAR_ENDPOINT,
  CANDIDATE_PORTAL_ENDPOINT,
  CAREERS_ENDPOINT,
  DASHBOARD_ENDPOINT,
  drawerWidth,
  getBaseUrl,
  HR_REPORTS_ENDPOINT,
  INTERVIEWS_ENDPOINT,
  LEVELS_ENDPOINT,
  LOGGED_IN_USER_INFO,
  LOGIN_ENDPOINT,
  MOBILE_VIEW_WIDTH,
  MY_OPEN_POSITIONS_ENDPOINT,
  ORGANIZATION_ENDPOINT,
  ORGANIZATION_VIEW_ENDPOINT,
  OtherContext,
  PANELS_ENDPOINT,
  POSITIONS_ENDPOINT,
  PRE_SCREENING,
  PREVIOUS_INTERVIEWS_ENDPOINT,
  REFERRALS_ENDPOINT,
  REPORTS_ENDPOINT,
  RESUME_SCREENING,
  ROLES,
  SETTINGS_ENDPOINT,
  SKILLS_ENDPOINT,
  STREAMS_ENDPOINT,
  TEAM_REQUIREMENTS_END_POINT,
  TEAMS_ENDPOINT,
  TEMPLATES_ENDPOINT,
  TOTAL_OPEN_POSITIONS_ENDPOINT,
  USER_LOGOUT_ENDPOINT,
  USER_SETTINGS_ENDPOINT,
  USERS_ENDPOINT,
} from '../constants';

import PrivateRoute from '../PrivateRoutes';

import RouterComponentUI from './RouterComponentUI';

import 'quill-mention/dist/quill.mention.min.css';

const MainContent = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  width: '100%',
  height: '100vh',
  position: 'relative',
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile || !isLogin() ? 0 : `-${drawerWidth}rem`,
  ...(open && {
    width: `calc(100% - ${drawerWidth}rem)`,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: theme.spacing(0),
  }),
}));

const HeaderDiv = styled('div')``;
const RootDiv = styled('div')({ display: 'flex' });

// Lazy-loaded components
const HRReports = lazy(() =>
  componentLazyLoader(() => import('../Pages/Reports'), ALLOWED_RETRY_LIMIT)
);
const LoginPage = lazy(() =>
  componentLazyLoader(() => import('../Pages/Login'), ALLOWED_RETRY_LIMIT)
);
const LogoutPage = lazy(() =>
  componentLazyLoader(
    () => import('../Components/LogInAndLogout/Logout/logout'),
    ALLOWED_RETRY_LIMIT
  )
);
const ViewPosition = lazy(() =>
  componentLazyLoader(() => import('../Pages/Position/View'), ALLOWED_RETRY_LIMIT)
);
const Positions = lazy(() =>
  componentLazyLoader(() => import('../Pages/Position/List'), ALLOWED_RETRY_LIMIT)
);
const Referrals = lazy(() =>
  componentLazyLoader(() => import('../Pages/Referrals/List'), ALLOWED_RETRY_LIMIT)
);
const ApplicationsBeta = lazy(() =>
  componentLazyLoader(() => import('../Pages/ApplicationsBeta/List'), ALLOWED_RETRY_LIMIT)
);
const ApplicationOverview = lazy(() =>
  componentLazyLoader(() => import('../Pages/PivotTable'), ALLOWED_RETRY_LIMIT)
);
const Dashboard = lazy(() =>
  componentLazyLoader(() => import('../Pages/Dashboard'), ALLOWED_RETRY_LIMIT)
);
const MyInterviews = lazy(() =>
  componentLazyLoader(() => import('../Pages/MyInterviews'), ALLOWED_RETRY_LIMIT)
);
const PreviousInterviews = lazy(() =>
  componentLazyLoader(() => import('../Pages/MyInterviews/previousInterviews'), ALLOWED_RETRY_LIMIT)
);
const Interview = lazy(() =>
  componentLazyLoader(() => import('../Pages/Interview'), ALLOWED_RETRY_LIMIT)
);
const GoogleCalender = lazy(() =>
  componentLazyLoader(() => import('../Pages/Interview/GoogleCalender'), ALLOWED_RETRY_LIMIT)
);
const ResumeScreening = lazy(() =>
  componentLazyLoader(() => import('../Pages/ResumeScreening'), ALLOWED_RETRY_LIMIT)
);
const PreScreening = lazy(() =>
  componentLazyLoader(() => import('../Pages/PreScreening'), ALLOWED_RETRY_LIMIT)
);
const Users = lazy(() =>
  componentLazyLoader(() => import('../Pages/Users/List'), ALLOWED_RETRY_LIMIT)
);
const Levels = lazy(() =>
  componentLazyLoader(() => import('../Pages/Level/List'), ALLOWED_RETRY_LIMIT)
);
const Skills = lazy(() =>
  componentLazyLoader(() => import('../Pages/Skill/List'), ALLOWED_RETRY_LIMIT)
);
const Streams = lazy(() =>
  componentLazyLoader(() => import('../Pages/Stream/List'), ALLOWED_RETRY_LIMIT)
);
const Templates = lazy(() =>
  componentLazyLoader(() => import('../Pages/Templates/List'), ALLOWED_RETRY_LIMIT)
);
const Teams = lazy(() =>
  componentLazyLoader(() => import('../Pages/Team/List'), ALLOWED_RETRY_LIMIT)
);
const TeamRequirements = lazy(() =>
  componentLazyLoader(() => import('../Pages/TeamRequirement/List'), ALLOWED_RETRY_LIMIT)
);
const Agencies = lazy(() =>
  componentLazyLoader(() => import('../Pages/Agency/List'), ALLOWED_RETRY_LIMIT)
);
const Page404 = lazy(() =>
  componentLazyLoader(() => import('../Pages/404/Page404'), ALLOWED_RETRY_LIMIT)
);
const OpenPositions = lazy(() =>
  componentLazyLoader(() => import('../Pages/MyOpenPositions'), ALLOWED_RETRY_LIMIT)
);
const TotalOpenPositions = lazy(() =>
  componentLazyLoader(() => import('../Pages/TotalOpenPositions'), ALLOWED_RETRY_LIMIT)
);
const ApplicationsDetails = lazy(() =>
  componentLazyLoader(() => import('../Pages/ApplicationDetails'), ALLOWED_RETRY_LIMIT)
);
const Settings = lazy(() =>
  componentLazyLoader(() => import('../Pages/Settings'), ALLOWED_RETRY_LIMIT)
);
const ApplicantDetails = lazy(() =>
  componentLazyLoader(() => import('../Pages/ApplicantDetails'), ALLOWED_RETRY_LIMIT)
);
const Organization = lazy(() =>
  componentLazyLoader(() => import('../Pages/Organization'), ALLOWED_RETRY_LIMIT)
);
const OrganisationView = lazy(() =>
  componentLazyLoader(() => import('../Pages/OrganisationView/index'), ALLOWED_RETRY_LIMIT)
);
const Panels = lazy(() =>
  componentLazyLoader(() => import('../Pages/Panel/List'), ALLOWED_RETRY_LIMIT)
);
const CalendarSetting = lazy(() =>
  componentLazyLoader(() => import('../Pages/UserSetting'), ALLOWED_RETRY_LIMIT)
);
const Reports = lazy(() =>
  componentLazyLoader(() => import('../Pages/PanelReports'), ALLOWED_RETRY_LIMIT)
);
const Page403 = lazy(() =>
  componentLazyLoader(() => import('../Pages/403/Page403'), ALLOWED_RETRY_LIMIT)
);
const CareersPage = lazy(() =>
  componentLazyLoader(() => import('../Pages/Careers'), ALLOWED_RETRY_LIMIT)
);
const FormApply = lazy(() =>
  componentLazyLoader(() => import('../Pages/Careers/ApplyForm'), ALLOWED_RETRY_LIMIT)
);
const CandidateLogin = lazy(() =>
  componentLazyLoader(() => import('../Pages/CandidatePortal/Login'), ALLOWED_RETRY_LIMIT)
);
const CandidatePosition = lazy(() =>
  componentLazyLoader(() => import('../Pages/CandidatePortal/PositionDetails'), ALLOWED_RETRY_LIMIT)
);
const CandidateDashboard = lazy(() =>
  componentLazyLoader(() => import('../Pages/CandidatePortal/Dashboard'), ALLOWED_RETRY_LIMIT)
);

function AdminAuthWrapper({ children }) {
  useAuth(LOGGED_IN_USER_INFO, getBaseUrl(), LOGIN_ENDPOINT, ACCESS_TOKEN_ENDPOINT, []);
  return children;
}

export default function Router() {
  const theme = useTheme();
  const location = useLocation();
  const windowSize = useWindowSize();
  const { isFullScreen } = useContext(OtherContext);
  const { userInfo } = useContext(AuthContext);
  const role = userInfo?.roles?.[0];

  const isCandidateRoute = location.pathname.startsWith(CANDIDATE_PORTAL_ENDPOINT);
  const isPublicRoute =
    location.pathname.startsWith(CAREERS_ENDPOINT) ||
    location.pathname.startsWith(APPLY) ||
    isCandidateRoute;
  const isOrganizationRoute = location.pathname === ORGANIZATION_ENDPOINT;
  const isForbiddenPage = location.pathname === ACCESS_DENIED_ENDPOINT;

  const alwaysAvailableRoutes = [
    [ORGANIZATION_ENDPOINT, <Organization key="ORGANIZATION_ENDPOINT" />],
    ['', <Dashboard key="DASHBOARD_ENDPOINT" />],
    [INTERVIEWS_ENDPOINT, <MyInterviews key="INTERVIEWS_ENDPOINT" />],
    [PREVIOUS_INTERVIEWS_ENDPOINT, <PreviousInterviews key="PREVIOUS_INTERVIEWS_ENDPOINT" />],
    [REFERRALS_ENDPOINT, <Referrals key="PREVIOUS_INTERVIEWS_ENDPOINT" />],
    [USER_SETTINGS_ENDPOINT, <CalendarSetting key="USER_SETTINGS_ENDPOINT" />],
  ];

  const nestedRoutes = [
    {
      path: POSITIONS_ENDPOINT,
      children: [
        { path: POSITIONS_ENDPOINT, element: <Positions /> },
        { path: ':id', element: <ViewPosition /> },
      ],
    },
  ];

  const conditionalRoutes = [
    ...(role !== ROLES.AGENCY
      ? [
          [APPLICATIONS_ENDPOINT, <ApplicationsBeta key="APPLICATIONS_ENDPOINT" />],
          [
            APPLICATIONS_ENDPOINT,
            <ApplicationsBeta key="APPLICATIONS_ENDPOINT:id" />,
            [{ path: ':id', element: <Interview /> }],
          ],
          [
            `${APPLICANT_DETAILS_ENDPOINT}/:id`,
            <ApplicantDetails key="APPLICANT_DETAILS_ENDPOINT" />,
          ],
          [CALENDAR_ENDPOINT, <GoogleCalender key="CALENDAR_ENDPOINT" />],
        ]
      : []),
    ...(![ROLES.USER, ROLES.AGENCY].includes(role)
      ? [
          [
            APPLICATION_OVERVIEW_ENDPOINT,
            <ApplicationOverview key="APPLICATION_OVERVIEW_ENDPOINT" />,
          ],
          [TEAMS_ENDPOINT, <Teams key="TEAMS_ENDPOINT" />],
          [PANELS_ENDPOINT, <Panels key="PANELS_ENDPOINT" />],
          [TEAM_REQUIREMENTS_END_POINT, <TeamRequirements key="TEAM_REQUIREMENTS_END_POINT" />],
          [LEVELS_ENDPOINT, <Levels key="LEVELS_ENDPOINT" />],
          [STREAMS_ENDPOINT, <Streams key="STREAMS_ENDPOINT" />],
          [TEMPLATES_ENDPOINT, <Templates key="TEMPLATES_ENDPOINT" />],
        ]
      : []),
    ...([ROLES.ADMIN, ROLES.SUPER_ADMIN].includes(role)
      ? [[ORGANIZATION_VIEW_ENDPOINT, <OrganisationView key="ORGANIZATION_VIEW_ENDPOINT" />]]
      : []),
    ...([ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TENANT_OWNER].includes(role)
      ? [
          [USERS_ENDPOINT, <Users key="USERS_ENDPOINT" />],
          [SKILLS_ENDPOINT, <Skills key="SKILLS_ENDPOINT" />],
          [AGENCIES_ENDPOINT, <Agencies key="AGENCIES_ENDPOINT" />],
          [HR_REPORTS_ENDPOINT, <HRReports key="HR_REPORTS_ENDPOINT" />],
          [REPORTS_ENDPOINT, <Reports key="PANEL_REPORTS_ENDPOINT" />],
        ]
      : []),
    ...([ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.HR, ROLES.TENANT_OWNER].includes(role)
      ? [
          [SETTINGS_ENDPOINT, <Settings key="SETTINGS_ENDPOINT" />],
          [PRE_SCREENING, <PreScreening key="PRE_SCREENING" />],
        ]
      : []),
    ...([ROLES.ADMIN, ROLES.HIRING_MANAGER, ROLES.SUPER_ADMIN, ROLES.TENANT_OWNER].includes(role)
      ? [[RESUME_SCREENING, <ResumeScreening key="RESUME_SCREENING" />]]
      : []),
    ...([
      ROLES.ADMIN,
      ROLES.HIRING_MANAGER,
      ROLES.SUPER_ADMIN,
      ROLES.HR,
      ROLES.TENANT_OWNER,
    ].includes(role)
      ? [
          [MY_OPEN_POSITIONS_ENDPOINT, <OpenPositions key="MY_OPEN_POSITIONS_ENDPOINT" />],
          [
            TOTAL_OPEN_POSITIONS_ENDPOINT,
            <TotalOpenPositions key="TOTAL_OPEN_POSITIONS_ENDPOINT" />,
          ],
          [
            APPLICATIONS_DETAILS_ENDPOINT,
            <ApplicationsDetails key="APPLICATIONS_DETAILS_ENDPOINT" />,
          ],
        ]
      : []),
  ];

  const privateRouteObjects = [
    ...alwaysAvailableRoutes.map(([path, element]) => ({
      path,
      element: <PrivateRoute />,
      children: [{ path, element }],
    })),
    ...nestedRoutes.map(({ path, children }) => ({
      path,
      element: <PrivateRoute />,
      children,
    })),
    ...conditionalRoutes.map(([path, element, children]) => ({
      path,
      element: <PrivateRoute />,
      children: children || [{ path, element }],
    })),
  ];

  const routes = [
    ...privateRouteObjects,
    { path: USER_LOGOUT_ENDPOINT, element: <LogoutPage /> },
    { path: LOGIN_ENDPOINT, element: <LoginPage /> },
    { path: ACCESS_DENIED_ENDPOINT, element: <Page403 /> },
    { path: DASHBOARD_ENDPOINT, element: <Navigate to="/" replace /> },
    { path: `${CAREERS_ENDPOINT}/:slug`, element: <CareersPage /> },
    { path: `${APPLY}/:slug`, element: <FormApply /> },
    { path: `${CANDIDATE_PORTAL_ENDPOINT}/:orgName`, element: <CandidateLogin /> },
    { path: `${CANDIDATE_PORTAL_ENDPOINT}/:orgName/position`, element: <CandidatePosition /> },
    { path: `${CANDIDATE_PORTAL_ENDPOINT}/:orgName/home`, element: <CandidateDashboard /> },
    {
      path: '*',
      element: <PrivateRoute />,
      children: [{ path: '*', element: <Page404 /> }],
    },
  ];

  const routing = useRoutes(routes);

  return (
    <HelmetProvider>
      {isPublicRoute ? (
        <Suspense fallback={<CompleteLoader />}>{routing}</Suspense>
      ) : (
        <AdminAuthWrapper>
          <RootDiv>
            {!isOrganizationRoute && !isForbiddenPage && <RouterComponentUI />}
            <MainContent open={isFullScreen} isMobile={windowSize.width <= MOBILE_VIEW_WIDTH}>
              {!isOrganizationRoute && isLogin() && (
                <HeaderDiv
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: theme.spacing(0, 1),
                    ...theme.mixins.toolbar,
                    justifyContent: 'flex-end',
                  }}
                />
              )}
              <Suspense fallback={<CompleteLoader />}>{routing}</Suspense>
            </MainContent>
          </RootDiv>
        </AdminAuthWrapper>
      )}
    </HelmetProvider>
  );
}
